import { api } from './client';
import type { ApiResponse } from './types';

/** The owner side of a business: its team, and the caller's invites to help run one. */

export interface TeamLocation { id: string; name: string; slug: string; city: string; state: string }
export interface TeamMember {
  memberId: string;
  user: { id: string; name: string; avatarUrl: string | null };
  role: string;
  status: 'invited' | 'active';
  invitedAt: string;
  acceptedAt: string | null;
  locations: TeamLocation[];
}
export interface BusinessInvite {
  memberId: string;
  role: string;
  invitedAt: string;
  invitedBy: { id: string; name: string; avatarUrl: string | null } | null;
  locations: TeamLocation[];
}

const segment = encodeURIComponent;

export const businessTeam = {
  team: (providerId: string) =>
    api.get<ApiResponse<{ members: TeamMember[] }>>(`/providers/${segment(providerId)}/team`),
  invite: (providerId: string, userId: string) =>
    api.post<ApiResponse<TeamMember>>(`/providers/${segment(providerId)}/team/invites`, { userId }),
  remove: (providerId: string, memberId: string) =>
    api.delete<void>(`/providers/${segment(providerId)}/team/${segment(memberId)}`),
  myInvites: () => api.get<ApiResponse<{ invites: BusinessInvite[] }>>('/business-invites'),
  respond: (memberId: string, accept: boolean) =>
    api.post<ApiResponse<{ memberId: string; status: 'active' | 'removed' }>>(
      `/business-invites/${segment(memberId)}/respond`,
      { accept },
    ),
};

/** Admin: send a pending claim back, telling the claimant what is missing. */
export function requestClaimChanges(claimId: string, memberMessage: string) {
  return api.post<ApiResponse<{ status: string }>>(`/admin/claims/${segment(claimId)}/request-changes`, { memberMessage });
}

/** "Meet the owner": the person behind a business, as a stranger reads them. */
export interface BusinessOwner {
  name: string;
  avatarUrl: string | null;
  roots?: Array<{ code: string | null; name: string; place: string | null }>;
  languages?: Array<{ code: string | null; name: string; nameNative: string | null; isNative: boolean }>;
}
export interface OwnerPresentation { show: boolean; parts: string[]; owner: BusinessOwner | null }

export const ownerPresentation = {
  get: (providerId: string) =>
    api.get<ApiResponse<OwnerPresentation>>(`/providers/${segment(providerId)}/owner-presentation`),
  set: (providerId: string, show: boolean, parts: string[]) =>
    api.put<ApiResponse<OwnerPresentation>>(`/providers/${segment(providerId)}/owner-presentation`, { show, parts }),
};
